import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, Pencil, Check, X, MapPinned, Briefcase, Building2, MapPin, Monitor, Clock, Wallet, Plus } from 'lucide-react';

import SarSymbol from '@/components/SarSymbol';
import AedSymbol from '@/components/AedSymbol';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { getAllCityCountryOptions, getIndustryOptions, COUNTRY_CITIES } from '@/data/locationData';
import { useTranslation } from 'react-i18next';
import { detectTextDirection } from '@/lib/textDirection';
import { COUNTRY_AR, CITY_AR, INDUSTRY_AR } from '@/data/arabicLookups';
import { SAFE_BADGE_TEXT, SAFE_COMPACT_VALUE, SAFE_TEXT_WRAP } from '@/lib/responsiveText';
import type { Json } from '@/integrations/supabase/types';

interface PreferenceProfile {
  preferred_roles?: Json;
  preferred_industries?: Json;
  preferred_locations?: Json;
  preferred_work_modes?: Json;
  preferred_employment_types?: Json;
  salary_expectation_min?: number | null;
  salary_expectation_max?: number | null;
  salary_currency?: string | null;
  willing_to_relocate?: boolean | null;
}

const stringArray = (value: Json | undefined): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

interface Props {
  profile: PreferenceProfile | null;
  onSave: (data: Record<string, unknown>) => void;
  saving: boolean;
  editLocked?: boolean;
  onEditingChange?: (active: boolean) => void;
}

export function PreferencesSection({ profile, onSave, saving, editLocked, onEditingChange }: Props) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    preferred_roles: [] as string[],
    preferred_industries: [] as string[],
    preferred_locations: [] as string[],
    preferred_work_modes: [] as string[],
    preferred_employment_types: [] as string[],
    salary_expectation_min: '',
    salary_expectation_max: '',
    salary_currency: 'SAR',
    willing_to_relocate: false,
  });
  const [roleInput, setRoleInput] = useState('');

  const industryOptions = useMemo(() => 
    getIndustryOptions().map(o => ({ ...o, label: isAr ? (INDUSTRY_AR[o.value] ?? o.value) : o.label })),
  [isAr]);
  const locationOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    for (const [country, cities] of Object.entries(COUNTRY_CITIES)) {
      for (const city of cities) {
        const val = `${city}, ${country}`;
        const label = isAr ? `${CITY_AR[city] ?? city}، ${COUNTRY_AR[country] ?? country}` : val;
        options.push({ label, value: val });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [isAr]);

  const startEdit = () => {
    if (editLocked) return;
    setForm({
      preferred_roles: stringArray(profile?.preferred_roles),
      preferred_industries: stringArray(profile?.preferred_industries),
      preferred_locations: stringArray(profile?.preferred_locations),
      preferred_work_modes: stringArray(profile?.preferred_work_modes),
      preferred_employment_types: stringArray(profile?.preferred_employment_types),
      salary_expectation_min: profile?.salary_expectation_min?.toString() ?? '',
      salary_expectation_max: profile?.salary_expectation_max?.toString() ?? '',
      salary_currency: profile?.salary_currency ?? 'SAR',
      willing_to_relocate: profile?.willing_to_relocate ?? false,
    });
    setEditing(true);
    onEditingChange?.(true);
  };

  const handleSave = () => {
    onSave({
      preferred_roles: form.preferred_roles,
      preferred_industries: form.preferred_industries,
      preferred_locations: form.preferred_locations,
      preferred_work_modes: form.preferred_work_modes,
      preferred_employment_types: form.preferred_employment_types,
      salary_expectation_min: form.salary_expectation_min ? Number(form.salary_expectation_min) : null,
      salary_expectation_max: form.salary_expectation_max ? Number(form.salary_expectation_max) : null,
      salary_currency: form.salary_currency,
      willing_to_relocate: form.willing_to_relocate,
    });
    setEditing(false);
    onEditingChange?.(false);
  };

  const toggleArray = (arr: string[], val: string) =>
    arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

  if (!editing) {
    const roles = stringArray(profile?.preferred_roles);
    const industries = stringArray(profile?.preferred_industries);
    const locations = stringArray(profile?.preferred_locations);
    const workModes = stringArray(profile?.preferred_work_modes);
    const empTypes = stringArray(profile?.preferred_employment_types);
    const hasSalary = profile?.salary_expectation_min && profile?.salary_expectation_max;

    return (
      <Card className="overflow-hidden">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-[var(--font-display)]">
              <Target className="w-5 h-5 text-seek" />
              {t('seek.profile.preferences')}
            </h3>
            <Button variant="ghost" size="sm" onClick={startEdit} disabled={editLocked} className="gap-1 bg-seek/10 text-seek hover:bg-seek hover:text-white">
              <Pencil className="w-4 h-4 me-1" /> {t('common.edit')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Target Roles */}
            <PrefCard
              icon={<Briefcase className="w-4 h-4 text-seek" />}
              label={t('seek.profile.targetRoles')}
              items={roles}
            />

            {/* Industries */}
            <PrefCard
              icon={<Building2 className="w-4 h-4 text-seek" />}
              label={t('seek.profile.industries')}
              items={industries}
              translateItem={isAr ? (v: string) => INDUSTRY_AR[v] ?? v : undefined}
            />

            {/* Locations */}
            <PrefCard
              icon={<MapPin className="w-4 h-4 text-seek" />}
              label={t('seek.profile.locations')}
              items={locations}
              translateItem={isAr ? (v: string) => {
                if (v.includes(', ')) { const [c, co] = v.split(', '); return `${CITY_AR[c] ?? c}، ${COUNTRY_AR[co] ?? co}`; }
                return v;
              } : undefined}
            />

            {/* Work Modes */}
            <PrefCard
              icon={<Monitor className="w-4 h-4 text-seek" />}
              label={t('seek.profile.workModes')}
              items={workModes}
            />

            {/* Employment Types */}
            <PrefCard
              icon={<Clock className="w-4 h-4 text-seek" />}
              label={t('seek.profile.employmentTypes')}
              items={empTypes}
            />

            {/* Salary Range */}
            <div className="rounded-xl border border-border bg-white p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-seek" />
                <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('seek.profile.salaryRange')}
                </span>
              </div>
              {hasSalary ? (
                <p className={cn('text-sm font-medium text-foreground', SAFE_COMPACT_VALUE)}>
                  {profile.salary_currency} {profile.salary_expectation_min.toLocaleString()} – {profile.salary_expectation_max.toLocaleString()}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">{t('seek.profile.notSpecified')}</p>
              )}
            </div>
          </div>

          {/* Relocate */}
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-border bg-white p-4">
            <MapPinned className="w-5 h-5 text-seek" />
            <span className="text-sm font-medium text-foreground flex-1">
              {t('seek.profile.willingToRelocate')}
            </span>
            <Badge
              variant={profile?.willing_to_relocate ? 'default' : 'outline'}
              className={cn(
                "text-xs font-semibold",
                profile?.willing_to_relocate && "bg-seek text-white"
              )}
            >
              {profile?.willing_to_relocate ? t('seek.profile.yes') : t('seek.profile.no')}
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5">
          <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-[var(--font-display)]">
            <Target className="w-5 h-5 text-seek" />
            {t('seek.profile.preferences')}
          </h3>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); onEditingChange?.(false); }}><X className="w-4 h-4" /></Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-seek text-white hover:bg-seek/90"><Check className="w-4 h-4 me-1" /> {t('common.save')}</Button>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.targetRoles')}</Label>
            <div className="mt-1 space-y-2">
              <div className="flex gap-2">
                <Input
                  value={roleInput}
                  onChange={(e) => setRoleInput(e.target.value)}
                  placeholder={t('seek.profile.targetRolesPlaceholder')}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const val = roleInput.replace(/,/g, '').trim();
                      if (val && !form.preferred_roles.includes(val)) {
                        setForm(p => ({ ...p, preferred_roles: [...p.preferred_roles, val] }));
                      }
                      setRoleInput('');
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!roleInput.trim()}
                  className="bg-seek text-white hover:bg-seek/90 shrink-0"
                  onClick={() => {
                    const val = roleInput.replace(/,/g, '').trim();
                    if (val && !form.preferred_roles.includes(val)) {
                      setForm(p => ({ ...p, preferred_roles: [...p.preferred_roles, val] }));
                    }
                    setRoleInput('');
                  }}
                >
                  <Plus className="w-4 h-4 me-1" /> {t('seek.profile.add')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.preferred_roles.map((role, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1 bg-seek/10 text-seek border-seek/20">
                    {role}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setForm(p => ({ ...p, preferred_roles: p.preferred_roles.filter((_, i) => i !== idx) }))} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.industries')}</Label>
            <div className="mt-1 space-y-2">
              <SearchableSelect
                value=""
                onValueChange={(v) => {
                  if (v && !form.preferred_industries.includes(v)) {
                    setForm(p => ({ ...p, preferred_industries: [...p.preferred_industries, v] }));
                  }
                }}
                options={industryOptions.filter(o => !form.preferred_industries.includes(o.value))}
                placeholder={t('seek.profile.addIndustry')}
                searchPlaceholder={t('seek.profile.searchIndustries')}
              />
              <div className="flex flex-wrap gap-1">
                {form.preferred_industries.map((ind, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1 bg-seek/10 text-seek border-seek/20">
                    {isAr ? (INDUSTRY_AR[ind] ?? ind) : ind}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setForm(p => ({ ...p, preferred_industries: p.preferred_industries.filter((_, i) => i !== idx) }))} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.preferredLocations')}</Label>
            <div className="mt-1 space-y-2">
              <SearchableSelect
                value=""
                onValueChange={(v) => {
                  if (v && !form.preferred_locations.includes(v)) {
                    setForm(p => ({ ...p, preferred_locations: [...p.preferred_locations, v] }));
                  }
                }}
                options={locationOptions.filter(o => !form.preferred_locations.includes(o.value))}
                placeholder={t('seek.profile.addLocation')}
                searchPlaceholder={t('seek.profile.searchLocations')}
              />
              <div className="flex flex-wrap gap-1">
                {form.preferred_locations.map((loc, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1 bg-seek/10 text-seek border-seek/20">
                    {isAr && loc.includes(', ') ? (() => { const [c, co] = loc.split(', '); return `${CITY_AR[c] ?? c}، ${COUNTRY_AR[co] ?? co}`; })() : loc}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setForm(p => ({ ...p, preferred_locations: p.preferred_locations.filter((_, i) => i !== idx) }))} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.workModes')}</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {['onsite', 'remote', 'hybrid'].map(m => (
                <Badge key={m} variant={form.preferred_work_modes.includes(m) ? 'default' : 'outline'} className={cn("cursor-pointer capitalize", form.preferred_work_modes.includes(m) && "bg-seek/15 text-seek border-seek/30 hover:bg-seek/25")} onClick={() => setForm(p => ({ ...p, preferred_work_modes: toggleArray(p.preferred_work_modes, m) }))}>{m}</Badge>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.employmentTypes')}</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {['full-time', 'part-time', 'contract', 'freelance', 'internship'].map(t2 => (
                <Badge key={t2} variant={form.preferred_employment_types.includes(t2) ? 'default' : 'outline'} className={cn("cursor-pointer capitalize", form.preferred_employment_types.includes(t2) && "bg-seek/15 text-seek border-seek/30 hover:bg-seek/25")} onClick={() => setForm(p => ({ ...p, preferred_employment_types: toggleArray(p.preferred_employment_types, t2) }))}>{t2}</Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <MapPinned className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{t('seek.profile.willingToRelocate')}</p>
                <p className="text-xs text-muted-foreground">{t('seek.profile.relocateDesc')}</p>
              </div>
            </div>
            <Switch
              checked={form.willing_to_relocate}
              onCheckedChange={(v) => setForm(p => ({ ...p, willing_to_relocate: v }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">{t('seek.profile.currency')}</Label>
              <Select value={form.salary_currency} onValueChange={(v) => setForm(p => ({ ...p, salary_currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAR"><span className="inline-flex items-center gap-1.5">SAR <SarSymbol size={12} /></span></SelectItem>
                  <SelectItem value="AED"><span className="inline-flex items-center gap-1.5">AED <AedSymbol size={12} /></span></SelectItem>
                  <SelectItem value="BHD">BHD (.د.ب)</SelectItem>
                  <SelectItem value="KWD">KWD (د.ك)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-muted-foreground">{t('seek.profile.minSalary')}</Label><Input type="number" value={form.salary_expectation_min} onChange={(e) => setForm(p => ({ ...p, salary_expectation_min: e.target.value }))} /></div>
            <div><Label className="text-xs text-muted-foreground">{t('seek.profile.maxSalary')}</Label><Input type="number" value={form.salary_expectation_max} onChange={(e) => setForm(p => ({ ...p, salary_expectation_max: e.target.value }))} /></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PrefCard({ icon, label, items, translateItem }: { icon: React.ReactNode; label: string; items: string[]; translateItem?: (v: string) => string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border bg-white p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className={cn('text-sm font-semibold text-muted-foreground uppercase leading-snug', SAFE_TEXT_WRAP)}>{label}</span>
      </div>
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((item, idx) => (
            <Badge key={idx} variant="secondary" className={cn('capitalize bg-seek/10 text-seek border-seek/20 font-medium h-auto py-1', SAFE_BADGE_TEXT)}>
              <span className={SAFE_BADGE_TEXT} dir={detectTextDirection(translateItem ? translateItem(item) : item)}>{translateItem ? translateItem(item) : item}</span>
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">{t('seek.profile.notSpecified')}</p>
      )}
    </div>
  );
}
